import { kvkGetBedrijf } from '@services/overig/orval-api/kvk/kvk';
import { type SUREbusinessManagementKvkKvkBedrijfDto as KvkBedrijfDto } from '@services/overig/orval-api/model/sUREbusinessManagementKvkKvkBedrijfDto';
import { ref, watch, type Ref } from 'vue';

const KVK_NUMMER_LENGTH = 8;
const NAME_SEARCH_MIN_LENGTH = 4;

interface UseKvkBedrijfSearchOptions {
  emptyKvkNummerValue?: string;
}

const optionLabel = (option?: KvkBedrijfDto) => `${option?.kvkNummer} | ${option?.naam}`;

export const useKvkBedrijfSearch = (
  kvkNummerModel?: Ref<string | undefined>,
  options?: UseKvkBedrijfSearchOptions,
) => {
  const modelValue = ref<KvkBedrijfDto | null>();
  const selectOptions = [] as KvkBedrijfDto[];
  const emptyKvkNummerValue =
    options && 'emptyKvkNummerValue' in options ? options.emptyKvkNummerValue : '';

  const searchFn = async (event: { query: string }) => {
    const { query } = event;

    const queryIsNumber = query.split('').every((x) => x >= '0' && x <= '9');

    if (
      (queryIsNumber && query.length !== KVK_NUMMER_LENGTH) ||
      query.length < NAME_SEARCH_MIN_LENGTH
    ) {
      return [];
    }

    const response = await kvkGetBedrijf({ search: query });
    const { result } = response;
    return Array.isArray(result) ? result : [];
  };

  if (kvkNummerModel !== undefined) {
    watch(
      kvkNummerModel,
      (newValue) => {
        if (newValue === modelValue.value?.kvkNummer) return;

        if ((newValue ?? '').length !== KVK_NUMMER_LENGTH) {
          modelValue.value = undefined;
          return;
        }

        searchFn({ query: newValue ?? '' }).then((result) => {
          if (Array.isArray(result) && result.length === 1) {
            modelValue.value = result[0];
          } else {
            modelValue.value = undefined;
          }
        });
      },
      { immediate: true },
    );
  }

  return {
    options: selectOptions,
    modelValue,
    props: {
      searchFn,
      autoCompleteDropdown: false,
      multiple: false,
      optionLabel,
      'onUpdate:modelValue': (value: KvkBedrijfDto[] | KvkBedrijfDto | undefined | null) => {
        modelValue.value = Array.isArray(value) ? value[0] || undefined : value;
        if (kvkNummerModel !== undefined) {
          kvkNummerModel.value = modelValue.value?.kvkNummer ?? emptyKvkNummerValue;
        }
      },
    },
  };
};
